import type { QuestCandidateStatus, QuestStatus, TaskStatus, WorkspaceMemberRole } from "@/lib/types";

// Domain predicates over quest and task status. They live in `src/lib` beside
// the `QuestStatus` type itself rather than under `features/quest` because the
// services layer needs them too (MockAdminService mirrors the same rules), and
// services must never import from features.

/**
 * Statuses a quest cannot transition out of. The same rule is enforced in the
 * SQL guards of `admin_close_quest` / `admin_cancel_quest` and in the
 * `quests_terminal_status` trigger (20260908170000); this is the TypeScript
 * side of it, shared so the UI and the mock adapter cannot drift apart.
 */
export const TERMINAL_QUEST_STATUSES: readonly QuestStatus[] = ["completed", "closed", "cancelled"];

export function isTerminalQuestStatus(status: QuestStatus): boolean {
  return TERMINAL_QUEST_STATUSES.includes(status);
}

/** A quest still open to status changes. */
export function isQuestOpen(status: QuestStatus): boolean {
  return !isTerminalQuestStatus(status);
}

/**
 * Statuses that mean the quest has already been started — work is underway, or
 * it has since ended. Composed from the terminal set rather than restating it,
 * so a status added there is covered here too.
 *
 * "locking" is deliberately NOT here. That is a start still in flight, and a
 * start that died mid-way must stay retryable by the creator.
 *
 * Why this is a predicate at all: `lockFundsAndStart` records the act ONLY on
 * the quest (status → "active", task → "assigned"); the proposal keeps its
 * "accepted" status for the rest of its life. So any surface asking "has this
 * been started?" has to read the quest, and must all read it the same way.
 */
export const STARTED_QUEST_STATUSES: readonly QuestStatus[] = [
  "active",
  ...TERMINAL_QUEST_STATUSES,
];

/**
 * `null`/`undefined` — a quest not loaded yet — is NOT started. Callers gate
 * start controls on this, and a loading quest must keep showing the control
 * rather than flicker it away from someone who still needs to press it.
 */
export function hasQuestStarted(status: QuestStatus | null | undefined): boolean {
  return !!status && STARTED_QUEST_STATUSES.includes(status);
}

/**
 * Task statuses that represent work a worker has started or delivered. Ending a
 * quest under any of these strands the worker's payment-release path, so the
 * admin RPCs refuse it — mirrored here for the mock adapter and the UI.
 */
export const IN_FLIGHT_TASK_STATUSES: readonly TaskStatus[] = ["in_progress", "submitted", "approved"];

export function isTaskInFlight(status: TaskStatus): boolean {
  return IN_FLIGHT_TASK_STATUSES.includes(status);
}

/**
 * Candidate statuses that mean "this person has put themselves forward" — the
 * worker-facing side of the funnel, as opposed to the sourcing side
 * ('suggested', 'reviewed', 'invited', 'dismissed').
 *
 * Pairs with the guard in `worker_apply_to_quest` (20260908180000), which
 * early-returns on exactly this set because re-tapping is a no-op for both: an
 * existing application has nothing to write, and a selection is terminal.
 * Named so the two stay recognisably the same rule.
 */
export const APPLIED_CANDIDATE_STATUSES: readonly QuestCandidateStatus[] = ["applied", "selected"];

export function hasAppliedToQuest(status: QuestCandidateStatus | null | undefined): boolean {
  return !!status && APPLIED_CANDIDATE_STATUSES.includes(status);
}

/**
 * What the retired start-CTA should SAY, given why it retired.
 *
 * `hasQuestStarted` is true for four statuses, and only ONE of them means work
 * began — the other three are endings. The CTA was right to disappear in all
 * four cases, but the message under it claimed "work is underway" over a quest
 * that had just been cancelled. Keyed off the status so each ending says what
 * actually happened; only the live case keeps the volunteer wording, because a
 * cancelled volunteer quest is simply cancelled.
 */
export function questEndedMessageKey(
  status: QuestStatus | null | undefined,
  isVolunteer: boolean,
): string {
  switch (status) {
    case "cancelled":
      return "negotiation.questCancelled";
    case "completed":
      return "negotiation.questCompleted";
    case "closed":
      return "negotiation.questClosed";
    default:
      return isVolunteer
        ? "volunteer.questAlreadyStarted"
        : "negotiation.questAlreadyStarted";
  }
}

/**
 * What a task's status badge should SAY and look like.
 *
 * DEV-318: `clone_quest_from_template` closes a stock quest's listing when the
 * last slot goes by parking the anchor task in "assigned" — with no assignee,
 * because no one is assigned to a listing. That is the right mechanism (the
 * null assignee is exactly what `release_template_slot` keys off to tell a
 * closed listing from real assigned work) but the wrong WORD to show a
 * creator: the page read "Assigned" with no name under it, inviting the
 * question "assigned to whom?". It is not assigned. It is full.
 *
 * Derived rather than stored, so it cannot drift from the task row.
 */
export function taskStatusBadge(
  task: { status: TaskStatus; assigneeId: string | null },
  isTemplateAnchor?: boolean | null,
): { variant: TaskStatus | "filled"; labelKey: string } {
  const isFilledListing =
    !!isTemplateAnchor && task.status === "assigned" && !task.assigneeId;

  return isFilledListing
    ? { variant: "filled", labelKey: "status.filled" }
    : { variant: task.status, labelKey: `status.${task.status}` };
}

/**
 * Which capacity chip a bounty card shows, if any.
 *
 * Two different facts used to share the word "slots", and the precedence
 * between them was re-typed in three JSX branches across GridBountyCard and
 * ListBountyRow — the kind of rule that drifts the first time one of them is
 * edited alone. It lives here as a pure function so it can be unit-tested and
 * stated once:
 *
 *   - a STOCK quest sells the same task N times, so the chip counts identical
 *     openings and answers "is there room for me";
 *   - a multi-task quest is N DIFFERENT jobs under one escrow, so the count is
 *     how much work is on offer, not how many people can take it.
 *
 * `maxSlots` wins when present: a stock quest's anchor also carries an open
 * task count, and reporting that instead would tell a worker there is one job
 * when there are N openings.
 */
export type BountySlotDisplay =
  | { kind: "slots"; filled: number; max: number }
  | { kind: "tasks"; count: number };

export function bountyCardSlotDisplay(card: {
  maxSlots?: number | null;
  filledSlots?: number | null;
  openTaskCount?: number | null;
}): BountySlotDisplay | null {
  if (card.maxSlots != null) {
    return { kind: "slots", filled: card.filledSlots ?? 0, max: card.maxSlots };
  }
  const count = card.openTaskCount ?? 0;
  return count > 0 ? { kind: "tasks", count } : null;
}

/**
 * What the workspace's budget line should state, or null to omit it.
 *
 * The line answers a different question for each side of the workspace. A
 * creator funds the whole quest, so the escrow total is exactly their figure.
 * A worker is paid for the task THEY hold, and once a quest may carry several
 * tasks negotiated with several workers those stopped being the same number:
 * the line showed a worker on one task of a 100+200+300 quest "600 USDT", on
 * the very screen where they do the work and get paid.
 *
 * A worker holding no task yet is the careful case. On a single-task quest the
 * quest total IS that task's pay, so the line stays honest and keeps rendering
 * exactly as it did. On a multi-task quest no honest figure exists, and the
 * rule is the same one the quest card model keeps: omit, never guess.
 *
 * 0 is a value, not an absence — a volunteer task returns 0 so the caller can
 * render "Volunteer" (DEV-246). Only null means "say nothing".
 *
 * `settled` is what keeps LOADING from impersonating an answer, and it is not
 * optional bookkeeping. Both of this function's inputs have a default that is
 * indistinguishable from a real reading: `userRole` is null until the
 * workspace-members query returns, and `tasks` is [] until the task-tree query
 * does. Together they read as "a creator on a single-task quest" — so a worker
 * opening a 100+200+300 quest was shown "600 USDT" on first paint, the very
 * number this function exists to stop showing them, before it flipped to 100.
 * A figure that is wrong for a moment is still wrong; omit until the answer is
 * in. (`membershipSettled` in `useWorkspaceContext` makes the same argument
 * for the same reason: a query that is loading has not answered.)
 */
export function workspaceBudgetUSD(input: {
  /**
   * Have BOTH the membership role and the task tree actually answered? False
   * while either is on its FIRST load or errored. A background refetch keeps
   * the previous answer, so it must not read as unsettled — the line would
   * blink off for every poll.
   */
  settled: boolean;
  userRole: WorkspaceMemberRole | null;
  userId: string | null | undefined;
  tasks: { budgetUSD: number; assigneeId: string | null }[];
  questTotalUSD: number;
}): number | null {
  const { settled, userRole, userId, tasks, questTotalUSD } = input;
  if (!settled) return null;

  // An absent role is a non-member, not the funder. `!== "worker"` handed the
  // escrow total to everyone the query could not place.
  if (userRole === null) return null;
  if (userRole !== "worker") return questTotalUSD;

  const own = userId ? tasks.filter((t) => t.assigneeId === userId) : [];
  if (own.length > 0) return own.reduce((sum, t) => sum + Number(t.budgetUSD), 0);

  return tasks.length <= 1 ? questTotalUSD : null;
}
